import * as React from 'react';

import {Asset} from '@deviceinsight/ng-ui-api-client';

import {Trans, useTranslation} from '@deviceinsight/ng-ui-scale-lib/api';
import formatTimestamp from '../../components/common/formatTimestamp';

const SHADOW_CONNECTION_STATE = 'dp.isOnline';
const SHADOW_LAST_UPDATE = 'dp.lastUpdate';

type ConnectionStateProps = {
	isOnline: boolean | null | undefined;
	lastUpdate?: string | null | undefined;
	showStateDescription?: boolean;
};

type Props = {
	asset: Asset;
	showStateDescription?: boolean;
};

const ONLINE_STATE = 'online';
const OFFLINE_STATE = 'offline';
const UNKNOWN_STATE = 'unknown';

export const formatShadowConnectionState = (shadowConnectionState: any): boolean | undefined => {
	switch (shadowConnectionState) {
		case 'online':
		case true:
			return true;

		case 'offline':
		case false:
			return false;

		default:
			return undefined;
	}
};

export const ConnectionState = ({isOnline, lastUpdate, showStateDescription}: ConnectionStateProps) => {
	const {t} = useTranslation();
	const stateString = isOnline === true ? ONLINE_STATE : isOnline === false ? OFFLINE_STATE : UNKNOWN_STATE;
	const lastUpdateString = lastUpdate || UNKNOWN_STATE;
	const connectionStatusTitle = t('asset.connectionState.title', {
		defaultValue: 'Connection status'
	});
	const lastUpdateTitle = t('asset.lastUpdated', {
		defaultValue: 'Last updated'
	});
	const connectionTooltip = `${connectionStatusTitle}: ${stateString}\n${lastUpdateTitle}: ${lastUpdateString}`;
	return (
		<span className={`connection-status ${stateString}`} title={connectionTooltip}>
			{showStateDescription && <Trans i18nKey={`asset.connectionState.${stateString}`}>{stateString}</Trans>}
		</span>
	);
};

const AssetConnectionState = ({asset, ...rest}: Props) => {
	const isOnline = asset.shadow && formatShadowConnectionState(asset.shadow[SHADOW_CONNECTION_STATE]);

	if (isOnline === undefined) {
		return null;
	}

	const lastUpdateValue = asset.shadow?.[SHADOW_LAST_UPDATE];
	const lastUpdate = lastUpdateValue === 'string' ? formatTimestamp(lastUpdateValue) : '';
	return <ConnectionState {...rest} isOnline={isOnline} lastUpdate={lastUpdate} />;
};

export default AssetConnectionState;
